import React, { useState, useEffect } from 'react';
import { withRouter, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Badge, Button, Icon, Layout, Modal, Row, Tooltip, Dropdown, Menu } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';

import { withAppContext } from 'context/AppContext';
import { getUserProfileById } from 'utils/apis/user';
import { DEFAULT_USER_PROFILE_URL } from 'utils/constants';
import { guard } from 'utils/general';
import { buildAPBillingInventoryUri, buildHostBookingEngineUrl, buildHostHotelBookingEngineUrl, buildUnifiedInboxUrl } from 'utils/routes';
import { LangContextConsumer } from '../../utils/LocaleProvider';
import { languageOptions } from '../../utils/constants';

import './Header.css';
import intl from 'react-intl-universal';
import { useSocket } from 'hooks/useSocket';
const AnnouncementsButton = ({ className, onClick, shouldShowAnnouncementDot }) => {
  return (
    <div className={className} onClick={onClick}>
      <Badge dot={shouldShowAnnouncementDot}>
        <Icon type="notification" theme="outlined" className="announcement-button-icon" />
      </Badge>
    </div>
  );
};

const AnnouncementsModal = ({ announcements, visible, onClose }) => {
  const [previewImageUrl, setPreviewImageUrl] = useState('');

  return (
    <>
      <Modal
        style={{ top: 20 }}
        bodyStyle={{
          maxHeight: '75vh',
          overflowY: 'auto'
        }}
        title={intl.get('header.headerLabels.whatsNew').d("What's New")}
        visible={visible}
        okText={intl.get('header.headerLabels.acknowledge').d('Acknowledge')}
        cancelText={intl.get('header.headerLabels.gotIt').d('Got it!')}
        onOk={() => onClose()}
        onCancel={() => onClose()}
      >
        {announcements.map(announcement => {
          return (
            <div className="announcement-post" key={announcement._id}>
              <ReactMarkdown
                source={announcement.message}
                renderers={{
                  image: props => (
                    <img
                      {...props}
                      role="button"
                      tabIndex={0}
                      onClick={() => setPreviewImageUrl(props.src || '')}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') setPreviewImageUrl(props.src || '');
                      }}
                      style={{
                        width: '100%',
                        height: 'auto',
                        cursor: props.src ? 'zoom-in' : 'default'
                      }}
                    />
                  )
                }}
              />
            </div>
          );
        })}
      </Modal>

      <Modal
        visible={!!previewImageUrl}
        footer={null}
        onCancel={() => setPreviewImageUrl('')}
        bodyStyle={{ textAlign: 'center' }}
        width="85vw"
        style={{ top: 20 }}
      >
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img src={previewImageUrl} style={{ maxWidth: '100%', maxHeight: '80vh', height: 'auto' }} />
      </Modal>
    </>
  );
};

const Header = ({
  user,
  announcements,
  isAllowedBookingEngine,
  isAllowedHotelBookingEngine,
  checkIsAdmin,
  checkIsAdminReadOnly,
  hasUnifiedInboxAccess,
  isCollapsed,
  history
}) => {
  const [imageUrl, setImageUrl] = useState(DEFAULT_USER_PROFILE_URL);
  const [isAnnouncementModalVisible, setIsAnnouncementModalVisible] = useState(false);
  const [shouldShowAnnouncementDot, setShouldShowAnnouncementDot] = useState(true);
  const [shouldShowButtons, setShouldShowButtons] = useState(true);

  const isUnifiedInboxEnabled = hasUnifiedInboxAccess();
  const { unreadMessageCount } = useSocket(isUnifiedInboxEnabled);

  useEffect(() => {
    if (user && user.roles && user.roles.length > 0 && user.roles[0].name === 'Owner') {
      setShouldShowButtons(false);
    }

    getUserProfileById(user._id)
      .then(res => {
        if (res.data.userProfile && res.data.userProfile.displayPicture && res.data.userProfile.displayPicture.imageUrl) {
          setImageUrl(res.data.userProfile.displayPicture.imageUrl);
        }
      })
      .catch(err => console.log(err));

    if (announcements && announcements.length > 0 && announcements[0]._id !== localStorage.getItem('latestAnnouncementReadId')) {
      setIsAnnouncementModalVisible(true);
      localStorage.setItem('latestAnnouncementReadId', announcements[0]._id);
    } else {
      setShouldShowAnnouncementDot(false);
    }
  }, [user, announcements]);

  const handleOnAdminPanelButtonClick = () => {
    history.push(buildAPBillingInventoryUri());
  };

  const handleOnAnnouncementModalOpen = () => {
    setIsAnnouncementModalVisible(true);
    setShouldShowAnnouncementDot(false);
  };

  const handleOnAnnouncementModalClose = () => {
    setIsAnnouncementModalVisible(false);
    setShouldShowAnnouncementDot(false);
  };

  const userProfileNavClassNames = isCollapsed ? 'header-firstname' : 'header-firstname no-show';
  const announcementButtonNavClassNames = isCollapsed ? 'announcement-button' : 'announcement-button no-show';
  const isOwner = user && user.roles && user.roles.length > 0 && user.roles[0].name === 'Owner';
  const hostId = guard(() => (isAllowedBookingEngine || isAllowedHotelBookingEngine ? user.roles[0].host : ''), '');

  return (
    <Layout.Header className="flex">
      <span>
        {checkIsAdmin() && !checkIsAdminReadOnly() && (
          <Button className="adminPanelButton" onClick={handleOnAdminPanelButtonClick}>
            To Admin Panel
          </Button>
        )}
      </span>
      <Row type="flex" align="middle">
        {!isOwner && announcements && announcements.length > 0 && (
          <>
            <Tooltip title={intl.get('header.headerLabels.whatsNew').d("What's New")}>
              <AnnouncementsButton
                className={announcementButtonNavClassNames}
                onClick={handleOnAnnouncementModalOpen}
                shouldShowAnnouncementDot={shouldShowAnnouncementDot}
              />
            </Tooltip>
            <AnnouncementsModal visible={isAnnouncementModalVisible} announcements={announcements} onClose={handleOnAnnouncementModalClose} />
          </>
        )}
        {isAllowedBookingEngine && shouldShowButtons && (
          <a href={buildHostBookingEngineUrl(hostId)} target="_blank" rel="noopener noreferrer">
            <Tooltip title={intl.get('header.headerLabels.homeBooking').d('Your Homes Booking Website')}>
              <Icon type="layout" className="booking-engine-icon" />
            </Tooltip>
          </a>
        )}
        {isAllowedHotelBookingEngine && shouldShowButtons && (
          <a href={buildHostHotelBookingEngineUrl(hostId)} target="_blank" rel="noopener noreferrer">
            <Tooltip title={intl.get('header.headerLabels.hotelBooking').d('Your Hotel Booking Website')}>
              <Icon type="layout" className="booking-engine-icon" />
            </Tooltip>
          </a>
        )}

        {shouldShowButtons && (
          <a href={buildUnifiedInboxUrl()} target="_blank" rel="noopener noreferrer" className={userProfileNavClassNames}>
            {/* ✅ Use unreadMessageCount from socket hook */}
            <div className="user-unified-inbox-icon">
              <Icon type="message" className="notifications" />
              {unreadMessageCount > 0 && (
                <span className="unread-badge" style={unreadMessageCount > 99 ? { fontSize: '11px', right: '2px' } : {}}>
                  {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                </span>
              )}
            </div>
          </a>
        )}

        {shouldShowButtons && (
          <Link to={`/notifications`} className={userProfileNavClassNames}>
            <Icon type="bell" className="notifications" />
          </Link>
        )}

        <Link to={`/userProfile/${user._id}`} className={userProfileNavClassNames}>
          <Tooltip title={intl.get('header.headerLabels.userProfile').d('User Profile')}>
            <img className="avatar" src={imageUrl} id="image" title="User profile" alt="User Icon" />
            {intl.get('header.headerLabels.hi').d('Hi,')} {user.firstName}!
          </Tooltip>
        </Link>

        <LangContextConsumer>
          {({ currentLocale, onSelectLocale }) => {
            const handleMenuClick = e => {
              onSelectLocale(e.key);
            };
            const langMenu = (
              <Menu onClick={handleMenuClick} defaultSelectedKeys={[currentLocale]}>
                {languageOptions.map((option, index) => (
                  <Menu.Item key={option.key}>
                    <img src={option.flagUrl} alt={option.label} style={{ marginRight: '15px', width: '20px', height: 'auto' }} />
                    {option.label}
                  </Menu.Item>
                ))}
              </Menu>
            );
            return (
              <Dropdown overlay={langMenu} placement="bottomRight">
                <a className="ant-dropdown-link" onClick={e => e.preventDefault()}>
                  <GlobalOutlined style={{ fontSize: '17px', marginLeft: '15px', paddingTop: '25px' }} />
                </a>
              </Dropdown>
            );
          }}
        </LangContextConsumer>
      </Row>
    </Layout.Header>
  );
};

export default withRouter(withAppContext(Header));
